import { create } from "zustand";
import { persist } from "zustand/middleware";
import { SAMPLE_VOTER_FLOW, type FlowGraph } from "@mensajeando/flows";
import type { MessageEvent } from "@mensajeando/reports";
import type {
  Agent,
  Campaign,
  Client,
  Contact,
  Conversation,
  ConsentRecord,
  Language,
  MessageTemplate,
  Organization,
} from "./types";

interface AppState {
  ui: { language: Language };
  setLanguage: (l: Language) => void;

  organizations: Organization[];
  clients: Client[];
  contacts: Contact[];
  consents: ConsentRecord[];
  campaigns: Campaign[];
  templates: MessageTemplate[];
  flows: Record<string, FlowGraph>;
  events: MessageEvent[];
  agents: Agent[];
  conversations: Conversation[];

  addContact: (c: Contact) => void;
  updateContact: (id: string, patch: Partial<Contact>) => void;
  addConsent: (c: ConsentRecord) => void;
  addCampaign: (c: Campaign) => void;
  updateCampaign: (id: string, patch: Partial<Campaign>) => void;
  addTemplate: (t: MessageTemplate) => void;
  upsertFlow: (f: FlowGraph) => void;
  appendEvent: (e: MessageEvent) => void;
  updateAgent: (id: string, patch: Partial<Agent>) => void;
  upsertConversation: (c: Conversation) => void;
  updateConversation: (id: string, patch: Partial<Conversation>) => void;
  resetSeed: () => void;
}

function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function seed(): Pick<
  AppState,
  "organizations" | "clients" | "contacts" | "consents" | "campaigns" | "templates" | "flows" | "events" | "agents" | "conversations"
> {
  const orgId = "org_main";
  const clientId = "client_demo";
  const flow = SAMPLE_VOTER_FLOW;
  const now = new Date().toISOString();

  // Original 5 + 20 in-flight seeded contacts for the agent/supervisor demo
  const seededNames: Array<[string, string, Language]> = [
    ["Maria",    "Hernandez",  "es"],
    ["Jose",     "Ramirez",    "es"],
    ["Ashley",   "Brown",      "en"],
    ["Daniel",   "Garcia",     "en"],
    ["Luz",      "Vega",       "es"],
    ["Carmen",   "Diaz",       "es"],
    ["Esteban",  "Lopez",      "es"],
    ["Jasmine",  "Ortiz",      "en"],
    ["Rafael",   "Mendoza",    "es"],
    ["Diana",    "Castro",     "es"],
    ["Brian",    "Nguyen",     "en"],
    ["Sofia",    "Reyes",      "es"],
    ["Andres",   "Salinas",    "es"],
    ["Camila",   "Pereira",    "es"],
    ["Isabela",  "Cruz",       "es"],
    ["Marcus",   "Johnson",    "en"],
    ["Yolanda",  "Acosta",     "es"],
    ["Tomas",    "Rivera",     "es"],
    ["Priscila", "Beltran",    "es"],
    ["Hector",   "Aguilar",    "es"],
    ["Nadia",    "Martinez",   "es"],
    ["Erika",    "Quintero",   "es"],
    ["Pablo",    "Soto",       "es"],
    ["Lucia",    "Herrera",    "es"],
    ["Mateo",    "Delgado",    "es"],
  ];
  const contacts: Contact[] = seededNames.map(([first, last, language], i) => ({
    id: `c${i + 1}`,
    organizationId: orgId,
    clientId,
    firstName: first,
    lastName: last,
    phoneE164: `+1404555${(101 + i).toString().padStart(4, "0")}`,
    language,
    timezone: "America/New_York",
    tags: i % 5 === 0 ? ["high_propensity"] : i % 7 === 0 ? ["first_time_voter"] : [],
    suppressed: i === 4, // Luz keeps her STOP history
    suppressionReason: i === 4 ? "Previous STOP" : undefined,
  }));

  const consents: ConsentRecord[] = contacts
    .filter((c) => !c.suppressed)
    .map((c) => ({
      id: uid("consent"),
      contactId: c.id,
      channel: "sms",
      language: c.language,
      status: "granted",
      source: "web_form",
      disclosureVersion: "2026-01-15.v1",
      at: now,
      ip: "203.0.113.10",
      userAgent: "Mozilla/5.0 (demo)",
      evidenceUrl: "https://example.org/signups/2026-06-01",
    }));

  const campaigns: Campaign[] = [
    {
      id: "camp_demo",
      organizationId: orgId,
      clientId,
      name: "Voter Intent — June 2026",
      type: "political",
      channel: "sms",
      language: "auto",
      flowId: flow.id,
      senderEligible: true,
      campaignRegistered: true,
      requiresConsent: true,
      createdAt: now,
      status: "live",
      startedConversations: 0,
      completedConversations: 0,
    },
  ];

  // Agents — phonebank floor for the demo
  const shiftStart = new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString();
  const agents: Agent[] = [
    { id: "agent_me",  organizationId: orgId, name: "You (demo agent)",  initials: "YO", languages: ["en", "es"], state: "available", shiftStartedAt: shiftStart, handledToday: 42, optOutsToday: 3, avgResponseSec: 38, escalations: 0 },
    { id: "agent_2",   organizationId: orgId, name: "Sandra Morales",    initials: "SM", languages: ["es", "en"], state: "available", shiftStartedAt: shiftStart, handledToday: 58, optOutsToday: 2, avgResponseSec: 31, escalations: 1 },
    { id: "agent_3",   organizationId: orgId, name: "Diego Pacheco",     initials: "DP", languages: ["es"],       state: "available", shiftStartedAt: shiftStart, handledToday: 51, optOutsToday: 4, avgResponseSec: 44, escalations: 0 },
    { id: "agent_4",   organizationId: orgId, name: "Karen Whitfield",   initials: "KW", languages: ["en"],       state: "wrap_up",   shiftStartedAt: shiftStart, handledToday: 39, optOutsToday: 1, avgResponseSec: 52, escalations: 0 },
    { id: "agent_5",   organizationId: orgId, name: "Luis Bermudez",     initials: "LB", languages: ["es", "en"], state: "break",     shiftStartedAt: shiftStart, handledToday: 47, optOutsToday: 5, avgResponseSec: 41, escalations: 2 },
    { id: "agent_6",   organizationId: orgId, name: "Tasha Reed",        initials: "TR", languages: ["en"],       state: "off",       handledToday: 0,  optOutsToday: 0, avgResponseSec: 0, escalations: 0 },
  ];

  // Conversation seeding — distribute 20 in-flight convos across contacts c6-c25
  const campaignId = "camp_demo";
  const minutesAgo = (m: number) => new Date(Date.now() - m * 60 * 1000).toISOString();
  const convoBlueprints: Array<{
    contactIdx: number; agentId?: string; state: Conversation["state"];
    lastInbound?: number; lastOutbound?: number; flagged?: boolean; escalationReason?: string;
  }> = [
    // Awaiting agent (oldest first → SLA pressure)
    { contactIdx: 5,  state: "awaiting_agent", lastInbound: 14, lastOutbound: 16 },
    { contactIdx: 6,  state: "awaiting_agent", lastInbound: 11, lastOutbound: 13 },
    { contactIdx: 7,  state: "awaiting_agent", lastInbound: 9,  lastOutbound: 12 },
    { contactIdx: 8,  state: "awaiting_agent", lastInbound: 7,  lastOutbound: 10 },
    { contactIdx: 9,  state: "awaiting_agent", lastInbound: 4,  lastOutbound: 8 },
    { contactIdx: 10, state: "awaiting_agent", lastInbound: 2,  lastOutbound: 6 },
    // Escalated
    { contactIdx: 11, state: "escalated", agentId: "agent_5", lastInbound: 22, lastOutbound: 24, flagged: true, escalationReason: "Voter requested supervisor" },
    { contactIdx: 12, state: "escalated", agentId: "agent_2", lastInbound: 18, lastOutbound: 19, flagged: true, escalationReason: "Possible reassigned number" },
    // Active with agent (awaiting voter)
    { contactIdx: 13, agentId: "agent_2", state: "awaiting_voter", lastInbound: 5,  lastOutbound: 1 },
    { contactIdx: 14, agentId: "agent_3", state: "awaiting_voter", lastInbound: 8,  lastOutbound: 2 },
    { contactIdx: 15, agentId: "agent_3", state: "awaiting_voter", lastInbound: 6,  lastOutbound: 1 },
    { contactIdx: 16, agentId: "agent_4", state: "awaiting_voter", lastInbound: 15, lastOutbound: 3 },
    { contactIdx: 17, agentId: "agent_me", state: "awaiting_voter", lastInbound: 9, lastOutbound: 2 },
    // Completed (done in last hour)
    { contactIdx: 18, agentId: "agent_2", state: "completed", lastInbound: 35, lastOutbound: 33 },
    { contactIdx: 19, agentId: "agent_3", state: "completed", lastInbound: 41, lastOutbound: 40 },
    { contactIdx: 20, agentId: "agent_me", state: "completed", lastInbound: 26, lastOutbound: 25 },
    // Opted out
    { contactIdx: 21, agentId: "agent_5", state: "opted_out", lastInbound: 50, lastOutbound: 51 },
    // Awaiting agent — English speakers for routing diversity
    { contactIdx: 22, state: "awaiting_agent", lastInbound: 3,  lastOutbound: 7 },
    { contactIdx: 23, state: "awaiting_agent", lastInbound: 1,  lastOutbound: 5 },
    { contactIdx: 24, agentId: "agent_me", state: "awaiting_voter", lastInbound: 12, lastOutbound: 2 },
  ];

  const conversations: Conversation[] = convoBlueprints.map((b, i) => {
    const contact = contacts[b.contactIdx]!;
    return {
      id: `conv_${i + 1}`,
      campaignId,
      contactId: contact.id,
      assignedAgentId: b.agentId,
      language: contact.language,
      state: b.state,
      lastInboundAt: b.lastInbound != null ? minutesAgo(b.lastInbound) : undefined,
      lastOutboundAt: b.lastOutbound != null ? minutesAgo(b.lastOutbound) : undefined,
      startedAt: minutesAgo((b.lastOutbound ?? 30) + 5),
      currentFlowNodeId: flow.entry,
      flagged: b.flagged,
      escalationReason: b.escalationReason,
    };
  });

  // Synthetic message events backing each conversation (one outbound + one inbound)
  const seededEvents: MessageEvent[] = [];
  const greetEN = "Hi {name}, this is Pulso Reach. Can we ask 3 quick questions about the June election? Reply STOP to opt out.";
  const greetES = "Hola {name}, le habla Pulso Reach. ¿Podemos hacerle 3 preguntas sobre las elecciones de junio? Conteste PARE para no recibir más.";
  const inboundEN = ["Sure", "ok go ahead", "yes", "who are you", "go"];
  const inboundES = ["Sí", "claro", "bueno", "adelante", "quien es"];
  conversations.forEach((conv, idx) => {
    const contact = contacts.find((c) => c.id === conv.contactId)!;
    const greet = (conv.language === "es" ? greetES : greetEN).replace("{name}", contact.firstName);
    if (conv.lastOutboundAt) {
      seededEvents.push({
        id: `ev_seed_o_${idx}`,
        campaignId, contactId: contact.id, channel: "sms", language: conv.language,
        direction: "outbound", status: "delivered", body: greet,
        flowNodeId: flow.entry, at: conv.lastOutboundAt,
      });
    }
    if (conv.lastInboundAt) {
      const pool = conv.language === "es" ? inboundES : inboundEN;
      seededEvents.push({
        id: `ev_seed_i_${idx}`,
        campaignId, contactId: contact.id, channel: "sms", language: conv.language,
        direction: "inbound", body: pool[idx % pool.length]!, at: conv.lastInboundAt,
      });
    }
    if (conv.state === "opted_out") {
      seededEvents.push({
        id: `ev_seed_stop_${idx}`,
        campaignId, contactId: contact.id, channel: "sms", language: conv.language,
        direction: "inbound", body: conv.language === "es" ? "PARE" : "STOP",
        at: minutesAgo(48), optOut: true,
      });
    }
  });

  return {
    organizations: [{ id: orgId, name: "Pulso Reach Demo Org", supportEmail: "support@example.org" }],
    clients: [{ id: clientId, organizationId: orgId, name: "Demo Civic Client" }],
    contacts,
    consents,
    campaigns,
    templates: [],
    flows: { [flow.id]: flow },
    events: seededEvents,
    agents,
    conversations,
  };
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      ui: { language: "en" },
      setLanguage: (language) => set((s) => ({ ui: { ...s.ui, language } })),

      ...seed(),

      addContact: (c) => set((s) => ({ contacts: [...s.contacts, c] })),
      updateContact: (id, patch) =>
        set((s) => ({
          contacts: s.contacts.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),
      addConsent: (c) => set((s) => ({ consents: [...s.consents, c] })),
      addCampaign: (c) => set((s) => ({ campaigns: [...s.campaigns, c] })),
      updateCampaign: (id, patch) =>
        set((s) => ({
          campaigns: s.campaigns.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),
      addTemplate: (t) => set((s) => ({ templates: [...s.templates, t] })),
      upsertFlow: (f) => set((s) => ({ flows: { ...s.flows, [f.id]: f } })),
      appendEvent: (e) => set((s) => ({ events: [...s.events, e] })),
      updateAgent: (id, patch) =>
        set((s) => ({ agents: s.agents.map((a) => (a.id === id ? { ...a, ...patch } : a)) })),
      upsertConversation: (c) =>
        set((s) => {
          const exists = s.conversations.some((x) => x.id === c.id);
          return {
            conversations: exists
              ? s.conversations.map((x) => (x.id === c.id ? c : x))
              : [...s.conversations, c],
          };
        }),
      updateConversation: (id, patch) =>
        set((s) => ({
          conversations: s.conversations.map((c) => (c.id === id ? { ...c, ...patch } : c)),
        })),
      resetSeed: () => set(() => ({ ...seed() })),
    }),
    { name: "mensajeando-store-v2" },
  ),
);

export { uid };
